/**
 * Manages dynamic CSS variables for layout elements (Header, Announcement Bar, Sub-navigation, sticky bottom bar).
 * Calculates heights and sets CSS variables for offsets and scroll padding.
 *
 * Automatically updates CSS variables when:
 * - Layout elements resize (via ResizeObserver)
 * - Window is resized (debounced)
 *
 * @example
 * import { initLayoutVariables } from './modules/layoutVariables.js';
 *
 * // Initialize once on page load
 * initLayoutVariables();
 *
 * // CSS variables will be automatically maintained:
 * // --header-offset
 * // --announcement-bar-offset
 * // --sub-navigation-offset
 * // --sticky-offset
 * // --scroll-padding (combined total)
 * // --bottom-offset
 */

import {LAYOUT_VARS, setCssVariable} from '../utils/cssVariables.js';

/**
 * Selectors for layout elements that affect scroll positioning.
 * We observe all matching elements and only measure visible ones.
 */
const LAYOUT_ELEMENTS = {
    header: '.js-navigationHeader, .js-minimalNavHeader',
    announcement: '#js-globalSiteAnnouncement',
    subNavigation: '.js-subNavigationMenu',
    bottomBar: '.js-stickyToBottomContainer',
};

const VISIBLE_LAYOUT_ELEMENTS = {
    header: '.js-navigationHeader:not(.hidden), .js-minimalNavHeader:not(.hidden)',
    announcement: '#js-globalSiteAnnouncement:not(.hidden)',
    subNavigation: '.js-subNavigationMenu:not(.hidden)',
    bottomBar: '.js-stickyToBottomContainer',
};

/**
 * Extra spacing (in pixels) added to scroll-padding to prevent content from
 * sitting flush against the fixed header.
 */
const SCROLL_PADDING_BUFFER = 10;

/** Delay used to debounce window resize updates. */
const WINDOW_RESIZE_DEBOUNCE_DELAY = 150;

/** @type {Set<Element>} */
const observedElements = new Set();

/** @type {ResizeObserver|null} */
let resizeObserver = null;

/** @type {boolean} */
let hasScheduledUpdate = false;

/** @type {number|null} */
let resizeTimeout = null;

/** @type {boolean} */
let isInitialized = false;

/**
 * Set CSS variable only when value changed to avoid redundant style recalculations.
 *
 * @param {string} variableName
 * @param {string} value
 * @returns {void}
 */
function setCssVariableIfChanged(variableName, value) {
    const currentValue = document.documentElement.style.getPropertyValue(variableName);
    if (currentValue === value) {
        return;
    }

    setCssVariable(variableName, value);
}

/**
 * Schedule a single layout update in the next animation frame.
 * Multiple calls before the frame runs are coalesced.
 *
 * @returns {void}
 */
function scheduleLayoutVariablesUpdate() {
    if (hasScheduledUpdate) {
        return;
    }

    hasScheduledUpdate = true;
    requestAnimationFrame(() => {
        hasScheduledUpdate = false;
        updateLayoutVariables();
    });
}

/**
 * Observe current layout elements if they are not observed yet.
 *
 * @returns {void}
 */
function observeLayoutElements() {
    if (!resizeObserver) {
        return;
    }

    Object.values(LAYOUT_ELEMENTS).forEach((selector) => {
        document.querySelectorAll(selector).forEach((element) => {
            if (observedElements.has(element)) {
                return;
            }

            resizeObserver.observe(element);
            observedElements.add(element);
        });
    });
}

/**
 * The bottom bar keeps its height while it is slid below the viewport or sticks in flow
 * further down the page, so the offset is the part of it that overlaps the viewport.
 *
 * @param {DOMRect} rect
 * @returns {number}
 */
function measureCoveredViewportBottom(rect) {
    return Math.max(0, Math.min(rect.height, window.innerHeight - rect.top));
}

/**
 * Updates the CSS variables based on current element dimensions.
 * Batches all DOM reads before writes to prevent layout thrashing.
 *
 * Safe to call repeatedly - performs efficiently even during animations/scrolling.
 *
 * @example
 * // Manually trigger an update (usually not needed)
 * updateLayoutVariables();
 */
export function updateLayoutVariables() {
    const header = document.querySelector(VISIBLE_LAYOUT_ELEMENTS.header);
    const announcement = document.querySelector(VISIBLE_LAYOUT_ELEMENTS.announcement);
    const subNavigation = document.querySelector(VISIBLE_LAYOUT_ELEMENTS.subNavigation);
    const bottomBar = document.querySelector(VISIBLE_LAYOUT_ELEMENTS.bottomBar);

    // Batch all DOM reads first (single reflow)
    const headerHeight = header ? header.getBoundingClientRect().height : 0;
    const announcementHeight = announcement ? announcement.getBoundingClientRect().height : 0;
    const subNavigationHeight = subNavigation ? subNavigation.getBoundingClientRect().height : 0;
    const bottomOffset = bottomBar ? measureCoveredViewportBottom(bottomBar.getBoundingClientRect()) : 0;

    // Then do all writes (no additional reflows)
    setCssVariableIfChanged(LAYOUT_VARS.headerOffset, `${headerHeight}px`);
    setCssVariableIfChanged(LAYOUT_VARS.announcementOffset, `${announcementHeight}px`);
    setCssVariableIfChanged(LAYOUT_VARS.subNavigationOffset, `${subNavigationHeight}px`);
    setCssVariableIfChanged(LAYOUT_VARS.bottomOffset, `${bottomOffset}px`);

    // The sticky stack: Header + SubNav + Announcement. Scroll padding adds a buffer on top.
    const stickyOffset = headerHeight + subNavigationHeight + announcementHeight;
    setCssVariableIfChanged(LAYOUT_VARS.stickyOffset, `${stickyOffset}px`);
    setCssVariableIfChanged(LAYOUT_VARS.scrollPadding, `${stickyOffset + SCROLL_PADDING_BUFFER}px`);
}

/**
 * Initialize the layout variable management system.
 * Sets up automatic updates via ResizeObserver and window resize events.
 *
 * @example
 * // Initialize once during application startup
 * initLayoutVariables();
 */
export function initLayoutVariables() {
    if (isInitialized) {
        scheduleLayoutVariablesUpdate();
        return;
    }

    isInitialized = true;

    // Initial update
    updateLayoutVariables();

    // Update on resize with proper debouncing
    window.addEventListener('resize', () => {
        if (resizeTimeout !== null) {
            clearTimeout(resizeTimeout);
        }

        resizeTimeout = setTimeout(() => {
            scheduleLayoutVariablesUpdate();
        }, WINDOW_RESIZE_DEBOUNCE_DELAY);
    });

    // Use ResizeObserver to track when layout elements change size
    resizeObserver = new ResizeObserver(() => {
        scheduleLayoutVariablesUpdate();
    });

    // Observe initial elements
    observeLayoutElements();

    // The bottom bar moves without resizing: it slides in with a transition and, in its sticky
    // variant, scrolls with the page. Neither reaches the ResizeObserver.
    document.querySelectorAll(LAYOUT_ELEMENTS.bottomBar).forEach((bottomBar) => {
        bottomBar.addEventListener('transitionend', () => scheduleLayoutVariablesUpdate());
    });
    if (document.querySelector(LAYOUT_ELEMENTS.bottomBar)) {
        window.addEventListener('scroll', () => scheduleLayoutVariablesUpdate(), {passive: true});
    }
}
